import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CloseOutlined,
  ExpandOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { GoogleMap, Marker, Polyline } from '@react-google-maps/api';
import { useSelector } from 'react-redux';
import { Button, Divider, Flex, Spin, Tag } from 'antd';
import { getMapStyles } from '@/common/GoogleMapStyle';
import type { RootState } from '@/redux-toolkit/store';
import { GetExecutionLegDetail } from '@/apis/shipment';
import type {
  CoLoadedCustomerLeg,
  ExecutionLegActiveCustomerLeg,
  ExecutionLegDetail,
  ExecutionLegRoute,
} from '@/apis/types';
import { useToken } from '@/common/helper';
import { parseShipmentDayjs } from '@/common/utils/utils';
import {
  GoogleMapsProvider,
  useGoogleMaps,
} from '@/components/GoogleMapsProvider';
import {
  getShipmentLegModeIcon,
  ShipmentLegModeIcon,
} from '@/assets/icons/icons';
import { formatQuantity } from '@/utils/formatQuantity';
import styles from './ExecutionLegDetailDrawer.module.css';

interface ExecutionLegDetailDrawerProps {
  elId: string | null | undefined;
  toggleDrawer: (
    state: 'open' | 'close',
    name: string,
    id: string | null,
    alert?: unknown,
    legType?: 'CL' | 'EL'
  ) => void;
}

const EMPTY_VALUE = '—';

const LabelValue: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Flex vertical gap={4} className={styles.minW0}>
    <span className={styles.label}>{label}</span>
    <span className={styles.value}>{value || EMPTY_VALUE}</span>
  </Flex>
);

const VitalsTag: React.FC<{ children: React.ReactNode; tone?: 'red' }> = ({
  children,
  tone,
}) => (
  <span
    className={`${styles.vitalsTag} ${tone === 'red' ? styles.vitalsTagRed : ''}`}
  >
    {children}
  </span>
);

const pluralize = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

const getLocationText = (location?: Record<string, unknown> | null): string => {
  if (!location) return '';
  return (
    String(
      location.address ||
        location.name ||
        location.locationCode ||
        location.location_code ||
        ''
    ) || ''
  );
};

const getRoutePointLabel = (
  point: ExecutionLegRoute['origin'] | ExecutionLegRoute['destination']
): string => point.name || point.address || point.location_code || EMPTY_VALUE;

const getActiveCustomerLegLabel = (
  activeCustomerLeg?: ExecutionLegActiveCustomerLeg | null
): string =>
  activeCustomerLeg?.customer_reference ||
  activeCustomerLeg?.reference_number ||
  activeCustomerLeg?.id ||
  'Active customer leg';

const formatDateTime = (value?: string | null): string => {
  if (!value) return EMPTY_VALUE;
  const parsed = parseShipmentDayjs(value);
  if (!parsed) return value;
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed.toDate());
};

const getModeIconType = (
  modeToken?: string | null,
  modeValue?: string | null
): ShipmentLegModeIcon => {
  const s = ((modeToken || modeValue) ?? '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
  if (s.includes('air') || s.includes('plane') || s.includes('flight'))
    return ShipmentLegModeIcon.Air;
  if (s.includes('linehaul')) return ShipmentLegModeIcon.Linehaul;
  if (s.includes('cartage')) return ShipmentLegModeIcon.Cartage;
  if (s.includes('clearance')) return ShipmentLegModeIcon.Clearance;
  return ShipmentLegModeIcon.Unknown;
};

const getProgressCount = (status?: string, clStatus?: string): number => {
  const s = (clStatus || status || '').toLowerCase();
  if (s.includes('deliver') || s.includes('complet')) return 4;
  if (s.includes('arriv') || s.includes('customs') || s.includes('depart'))
    return 3;
  if (
    s.includes('transit') ||
    s.includes('pickup') ||
    s.includes('in_progress')
  )
    return 2;
  return 1;
};

const getCustomerLegKey = (leg: CoLoadedCustomerLeg): string =>
  leg.id ||
  leg.shipment_id ||
  leg.reference_number ||
  leg.customer_reference ||
  '';

// ─── Map ───────────────────────────────────────────────────────────────────

const mapContainerStyle: React.CSSProperties = {
  width: '100%',
  height: 250,
  borderRadius: 6,
};

const ExecutionLegRouteMap: React.FC<{
  route?: ExecutionLegRoute | null;
  activeCustomerLeg?: ExecutionLegActiveCustomerLeg | null;
}> = ({ route, activeCustomerLeg }) => {
  const mapsContext = useGoogleMaps();
  const darkModeEnabled = useSelector(
    (state: RootState) => state.appearance.darkModeEnabled
  );
  const sensorLocation = activeCustomerLeg?.latest_location;
  // Route line: green in dark mode (consistent with ShipmentMap/
  // ShipmentPreviewMap) so it reads clearly against the dark tiles;
  // unchanged blue in light mode. Maps symbol/polyline colors are plain
  // hex, not CSS custom properties.
  const routeOptions = useMemo(
    () => ({
      strokeColor: darkModeEnabled ? '#34D399' : '#266EF1',
      strokeOpacity: 0.78,
      strokeWeight: 3,
      clickable: false,
      geodesic: true,
      zIndex: 2,
    }),
    [darkModeEnabled]
  );

  if (!route?.origin || !route.destination) return null;

  const center = sensorLocation || route.origin;
  const sensorLabel = getActiveCustomerLegLabel(activeCustomerLeg);

  if (!mapsContext?.isLoaded) {
    return <div className={styles.mapUnavailable}>Map unavailable</div>;
  }

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={center}
      zoom={sensorLocation ? 7 : 4}
      options={{
        clickableIcons: false,
        disableDefaultUI: true,
        gestureHandling: 'none',
        styles: getMapStyles(darkModeEnabled),
      }}
    >
      <Polyline
        path={[route.origin, route.destination]}
        options={routeOptions}
      />
      {sensorLocation ? (
        <Marker position={sensorLocation} title={sensorLabel} />
      ) : null}
    </GoogleMap>
  );
};

// ─── CL Card ───────────────────────────────────────────────────────────────

const ProgressBar: React.FC<{ filled: number; total?: number }> = ({
  filled,
  total = 4,
}) => (
  <div
    aria-hidden='true'
    className={styles.progressBar}
    // eslint-disable-next-line react/forbid-dom-props -- column count comes from the `total` prop
    style={{ gridTemplateColumns: `repeat(${total}, 1fr)` }}
  >
    {Array.from({ length: total }, (_, i) => (
      <span
        key={i}
        className={`${styles.progressSegment} ${i < filled ? styles.progressSegmentFilled : ''}`}
      />
    ))}
  </div>
);

const CoLoadedCustomerLegCard: React.FC<{
  leg: CoLoadedCustomerLeg;
  modeIconType: ShipmentLegModeIcon;
  carrierName?: string;
  onOpenCustomerLeg: (shipmentId: string) => void;
}> = ({ leg, modeIconType, carrierName, onOpenCustomerLeg }) => {
  const title =
    leg.customer_reference || leg.reference_number || leg.id || EMPTY_VALUE;
  const progressCount = getProgressCount(leg.status, leg.cl_status);

  return (
    <div className={styles.clCard}>
      <Flex justify='space-between' align='flex-start' gap={12}>
        <Flex vertical gap={8} style={{ flex: 1, minWidth: 0 }}>
          {/* Row 1: mode icon + reference + status tag */}
          <Flex gap={8} align='center' wrap='wrap'>
            <span className={styles.modeIconSm}>
              {getShipmentLegModeIcon(modeIconType, {
                width: 14,
                height: 14,
                color: 'var(--text-secondary)',
              })}
            </span>
            <span className={styles.valueBold}>{title}</span>
            {leg.status ? (
              <Tag color='processing' style={{ marginInlineEnd: 0 }}>
                {leg.status}
              </Tag>
            ) : null}
          </Flex>

          {/* Row 2: carrier */}
          {carrierName ? (
            <Flex gap={4} align='center'>
              <span className={styles.labelSm}>Carrier</span>
              <span className={styles.carrierSeparator}>·</span>
              <span className={styles.valueSm}>{carrierName}</span>
            </Flex>
          ) : null}

          {/* Row 3: progress bar */}
          <ProgressBar filled={progressCount} />
        </Flex>

        {/* Arrow button */}
        <Button
          aria-label='Open customer leg'
          disabled={!leg.shipment_id}
          icon={<ArrowRightOutlined />}
          size='small'
          style={{ flexShrink: 0 }}
          onClick={() => {
            if (leg.shipment_id) onOpenCustomerLeg(leg.shipment_id);
          }}
        />
      </Flex>
    </div>
  );
};

// ─── Alert banner ──────────────────────────────────────────────────────────

const AlertBanner: React.FC<{ alertCount: number }> = ({ alertCount }) => (
  <div className={styles.alertBanner}>
    <Flex gap={12} align='flex-start'>
      <WarningOutlined
        style={{ color: 'var(--warning)', fontSize: 16, marginTop: 2 }}
      />
      <Flex vertical gap={4} style={{ flex: 1 }}>
        <span className={styles.valueBold}>
          {pluralize(alertCount, 'unresolved alert')}
        </span>
        <span className={styles.labelSm}>
          Review the co-loaded customer legs for details.
        </span>
      </Flex>
    </Flex>
  </div>
);

// ─── Main drawer ───────────────────────────────────────────────────────────

const ExecutionLegDetailDrawer: React.FC<ExecutionLegDetailDrawerProps> = ({
  elId,
  toggleDrawer,
}) => {
  const { token } = useToken();
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<ExecutionLegDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!elId || !token) return;
    setLoading(true);
    setError(null);
    GetExecutionLegDetail(token, elId)
      .then((res) => {
        setDetail(res);
      })
      .catch(() => {
        setError('Failed to load execution leg details.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [elId, token]);

  const onClose = () => toggleDrawer('close', 'ELDetail', null);
  const onOpenCustomerLeg = (shipmentId: string) =>
    toggleDrawer('open', 'Detail', shipmentId, undefined, 'CL');

  const reference = detail?.mbl || detail?.mawb || detail?.id || EMPTY_VALUE;
  const mode = detail?.mode?.name || detail?.mode?.value || EMPTY_VALUE;
  const modeIconType = getModeIconType(
    detail?.mode?.icon_token,
    detail?.mode?.value
  );
  const originAddr = getLocationText(detail?.origin);
  const destAddr = getLocationText(detail?.destination);
  const productsPreview = detail?.products_preview ?? [];
  const productsCount = detail?.products_count ?? productsPreview.length;
  const productName =
    detail?.product?.name || detail?.product?.product_name || undefined;
  const productTemperature =
    detail?.product?.aggregated_temperature_range ||
    detail?.product?.temperature_range ||
    undefined;
  const coLoadedLegs = detail?.customer_legs ?? [];
  const sensorLocationLabel =
    detail?.active_customer_leg?.latest_location?.address ||
    (detail?.active_customer_leg?.latest_location
      ? 'Live CL sensor'
      : 'No live CL sensor');

  const eventRows = useMemo(
    () =>
      ((detail?.events ?? []) as Record<string, unknown>[]).map((event, i) => ({
        id: `${i}-${String(event.id || event.type || event.description || '')}`,
        title: String(event.description || event.type || 'Event'),
        meta: String(event.timestamp || event.created_at || ''),
      })),
    [detail?.events]
  );

  return (
    <div className={`h-full ${styles.root}`}>
      {/* ── Header ── */}
      <Flex
        align='center'
        justify='space-between'
        style={{
          borderBottom: '1px solid var(--gray-300)',
          height: 52,
          padding: '0 16px 0 12px',
        }}
      >
        <Button
          aria-label='Back'
          icon={<ArrowLeftOutlined />}
          onClick={onClose}
          type='text'
        />
        <Flex gap={4} align='center'>
          <Button aria-label='Expand' icon={<ExpandOutlined />} type='text' />
          <Button
            aria-label='Close'
            icon={<CloseOutlined />}
            onClick={onClose}
            type='text'
          />
        </Flex>
      </Flex>

      {loading ? (
        <Flex justify='center' align='center' style={{ height: 240 }}>
          <Spin />
        </Flex>
      ) : error ? (
        <div className={styles.error}>{error}</div>
      ) : detail ? (
        <>
          {/* ── Title ── */}
          <div className={styles.titleBlock}>
            <span className={styles.labelBlockMb4}>Execution leg</span>
            <Flex align='center' gap={8} wrap='wrap'>
              <span className={styles.modeIconMd}>
                {getShipmentLegModeIcon(modeIconType, {
                  width: 16,
                  height: 16,
                  color: 'var(--text-secondary)',
                })}
              </span>
              <span className={styles.referenceTitle}>{reference}</span>
              {detail.team_count > 0 ? (
                <VitalsTag>{pluralize(detail.team_count, 'team')}</VitalsTag>
              ) : null}
            </Flex>
            {detail.mawb && detail.mawb !== reference ? (
              <span className={styles.labelBlockMt4}>MAWB {detail.mawb}</span>
            ) : null}
          </div>

          {/* ── Alert banner ── */}
          {detail.alert_count > 0 ? (
            <div className={styles.padAlert}>
              <AlertBanner alertCount={detail.alert_count} />
            </div>
          ) : null}

          {/* ── Mode + Status row ── */}
          <div className={styles.padMode}>
            <div className={styles.modeGrid}>
              <LabelValue label='Mode' value={mode} />
              <LabelValue label='Status' value={detail.status} />
            </div>
          </div>

          {/* ── Map ── */}
          {detail.route ? (
            <div className={styles.padMode}>
              <GoogleMapsProvider>
                <ExecutionLegRouteMap
                  route={detail.route}
                  activeCustomerLeg={detail.active_customer_leg}
                />
              </GoogleMapsProvider>
              <Flex justify='space-between' gap={16} style={{ marginTop: 12 }}>
                <LabelValue
                  label='Origin'
                  value={getRoutePointLabel(detail.route.origin)}
                />
                <LabelValue
                  label='Destination'
                  value={getRoutePointLabel(detail.route.destination)}
                />
              </Flex>
            </div>
          ) : null}

          {/* ── Shipment details ── */}
          <div className={styles.padDetails}>
            <Flex
              justify='space-between'
              align='center'
              style={{ marginBottom: 16 }}
            >
              <span className={styles.sectionTitle}>Shipment details</span>
              <Flex gap={6} wrap='wrap' justify='flex-end'>
                <VitalsTag>{pluralize(detail.co_loaded_count, 'CL')}</VitalsTag>
                <VitalsTag tone={detail.alert_count > 0 ? 'red' : undefined}>
                  {pluralize(detail.alert_count, 'alert')}
                </VitalsTag>
                {detail.is_rfs ? <VitalsTag>RFS</VitalsTag> : null}
              </Flex>
            </Flex>
            <div className={styles.detailsGrid}>
              <LabelValue label='Carrier' value={detail.carrier_name} />
              <LabelValue label='SCAC' value={detail.carrier_scac} />
              <LabelValue label='Origin' value={originAddr} />
              <LabelValue label='Destination' value={destAddr} />
              <LabelValue
                label='Earliest ETA'
                value={formatDateTime(detail.eta)}
              />
              <LabelValue
                label='Active CL sensor'
                value={sensorLocationLabel}
              />
            </div>
          </div>

          <Divider style={{ margin: '28px 0 0' }} />

          {/* ── Live chain of custody ── */}
          <div className={styles.section}>
            <Flex
              justify='space-between'
              align='center'
              style={{ marginBottom: 12 }}
            >
              <span className={styles.sectionTitle}>Live chain of custody</span>
              <Button size='small' type='text'>
                View all
              </Button>
            </Flex>
            <Flex vertical gap={8}>
              {coLoadedLegs.length > 0 ? (
                coLoadedLegs.map((cl) => (
                  <CoLoadedCustomerLegCard
                    key={getCustomerLegKey(cl)}
                    leg={cl}
                    modeIconType={modeIconType}
                    carrierName={detail.carrier_name}
                    onOpenCustomerLeg={onOpenCustomerLeg}
                  />
                ))
              ) : (
                <span className={styles.labelSm}>
                  No co-loaded customer legs visible.
                </span>
              )}
            </Flex>
          </div>

          <Divider style={{ margin: 0 }} />

          {/* ── Products ── */}
          {productsCount > 0 || productName ? (
            <>
              <div className={styles.section}>
                <Flex
                  justify='space-between'
                  align='center'
                  style={{ marginBottom: 16 }}
                >
                  <span className={styles.sectionTitle}>
                    Products ({productsCount})
                  </span>
                  {productTemperature ? (
                    <Tag style={{ marginInlineEnd: 0 }}>
                      {productTemperature}
                    </Tag>
                  ) : null}
                </Flex>
                {productName ? (
                  <div className={styles.mb16}>
                    <LabelValue label='Aggregate product' value={productName} />
                  </div>
                ) : null}
                {productsPreview.length > 0 ? (
                  <>
                    <Flex
                      style={{
                        borderBottom: '1px solid var(--gray-300)',
                        paddingBottom: 8,
                      }}
                    >
                      <span className={styles.labelFlex1}>Name</span>
                      <span className={styles.labelQty}>Qty</span>
                      <span className={styles.labelFlex1}>Serials</span>
                    </Flex>
                    {productsPreview.map((product, index) => (
                      <Flex
                        key={product.id || `${product.name}-${index}`}
                        align='center'
                        style={{
                          borderBottom: '1px solid var(--dark-border)',
                          minHeight: 48,
                          gap: 8,
                        }}
                      >
                        <span className={styles.valueFlex1}>
                          {product.name || EMPTY_VALUE}
                        </span>
                        <span className={styles.valueQty}>
                          {product.quantity != null
                            ? formatQuantity(product.quantity)
                            : EMPTY_VALUE}
                        </span>
                        <span className={styles.valueFlex1}>
                          {product.serial_preview?.join(', ') || EMPTY_VALUE}
                        </span>
                      </Flex>
                    ))}
                  </>
                ) : null}
              </div>
              <Divider style={{ margin: 0 }} />
            </>
          ) : null}

          {/* ── Controlling office ── */}
          <div className={styles.section}>
            <span className={styles.sectionTitleBlock}>Controlling office</span>
            <div className={styles.detailsGrid}>
              {/* The leg's Pickup RTG before transit, its Drop RTG after. */}
              <LabelValue
                label='Current owner'
                value={detail.controlling_office}
              />
              <LabelValue label='Pickup RTG' value={detail.pickup_rtg} />
              <LabelValue label='Drop RTG' value={detail.drop_rtg} />
            </div>
          </div>

          {/* ── Chain of custody timeline ── */}
          {eventRows.length > 0 ? (
            <>
              <Divider style={{ margin: 0 }} />
              <div className={styles.sectionLast}>
                <Flex
                  justify='space-between'
                  align='center'
                  style={{ marginBottom: 16 }}
                >
                  <span className={styles.sectionTitle}>Chain of custody</span>
                  <Flex gap={8}>
                    <Button size='small'>Comment</Button>
                    <Button size='small'>Upload file</Button>
                  </Flex>
                </Flex>
                <Flex vertical>
                  {eventRows.map((event, index) => (
                    <Flex key={event.id} gap={16} style={{ minHeight: 56 }}>
                      <Flex vertical align='center' style={{ width: 24 }}>
                        <span
                          className={`${styles.timelineDot} ${index === 0 ? styles.timelineDotActive : ''}`}
                        />
                        {index < eventRows.length - 1 ? (
                          <span className={styles.timelineLine} />
                        ) : null}
                      </Flex>
                      <Flex vertical gap={2} style={{ flex: 1, minWidth: 0 }}>
                        <span className={styles.value}>{event.title}</span>
                        {event.meta ? (
                          <span className={styles.label}>{event.meta}</span>
                        ) : null}
                      </Flex>
                    </Flex>
                  ))}
                </Flex>
              </div>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default ExecutionLegDetailDrawer;
